import { Icon, Link } from '@chakra-ui/react';
import { AiFillCaretDown, AiFillCaretRight } from 'react-icons/ai';
import { ReactNode, useEffect } from 'react';
import { KText } from '@/ui-library/components';

interface HeaderProps {
  accessor: string;
  title: string;
  width?: number | string;
  sort?: boolean;
  expand?: boolean;
  isReadOnly?: boolean;
  renderFooter?: (value: any, row: any) => ReactNode;
  groupBy?: string;
  breakDownBy?: string;
  columnConfig?: boolean;
}

// Helper function to format month strings for display
const formatMonthString = (monthString: string) => {
  if (!/^\d{4}-\d{2}$/.test(monthString)) return monthString;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [year, month] = monthString.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  return `${months[monthIndex]} ${year}`;
};

export const renderHeader = ({
  accessor,
  title,
  width,
  sort = false,
  expand = false,
  renderFooter,
  isReadOnly,
  groupBy,
  breakDownBy,
  columnConfig = true,
}: HeaderProps) => {
  return {
    accessor,
    Header: ({
      getToggleAllRowsExpandedProps,
      isAllRowsExpanded,
      toggleAllRowsExpanded,
    }) => {
      // Automatically expand all rows if isReadOnly is true
      useEffect(() => {
        if (isReadOnly && !isAllRowsExpanded) {
          toggleAllRowsExpanded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isReadOnly, isAllRowsExpanded, toggleAllRowsExpanded]);

      return (
        <KText
          size="sm-semi-bold"
          display="flex"
          alignItems="center"
          textTransform="initial"
        >
          {expand && (
            <Icon
              as={isAllRowsExpanded ? AiFillCaretDown : AiFillCaretRight}
              w="16px"
              h="16px"
              {...getToggleAllRowsExpandedProps()}
              onClick={(e) => {
                e.stopPropagation();
                getToggleAllRowsExpandedProps()?.onClick?.(e);
              }}
            />
          )}
          {title}
        </KText>
      );
    },
    width,
    sort,
    Footer: renderFooter,
    Cell: ({ row, cell }) => {
      // Determine if this cell should be a job link based on the actual data
      const shouldBeJobLink = () => {
        // Check if the current row data contains job information
        const rowData = row.original;
        const isMainRow = row.depth === 0;

        // For main rows (depth 0), check if groupBy is job_name
        if (isMainRow && groupBy === 'job_name' && rowData.g1?.id) {
          return true;
        }

        // For sub-rows (depth 1), check if breakDownBy is job_name
        if (!isMainRow && breakDownBy === 'job_name' && rowData.g2?.id) {
          return true;
        }

        return false;
      };

      const isJobLink = shouldBeJobLink();
      const jobId = row.depth === 0 ? row.original.g1?.id : row.original.g2?.id;

      // Format the display value for Time grouping (month strings)
      const isTimeGrouping = groupBy === null || groupBy === 'dt' || groupBy === undefined;
      const displayValue = isTimeGrouping ? formatMonthString(cell?.value) : cell?.value;

      if (!row.canExpand) {
        return (
          <KText
            fontWeight={row.canExpand && row.depth === 0 ? '650' : '550'}
            size="sm"
            paddingLeft={row?.depth > 0 ? '16px' : '0'}
          >
            {isJobLink && jobId ? (
              <Link
                href={`/jobs/${jobId}`}
                target="_blank"
                rel="noreferrer"
                _hover={{ textDecor: 'underline' }}
              >
                {displayValue}
              </Link>
            ) : (
              displayValue
            )}
          </KText>
        );
      }

      return (
        <KText
          size="sm"
          fontWeight={row.canExpand && row.depth === 0 ? '650' : '550'}
          display="flex"
          alignItems="center"
          {...row.getToggleRowExpandedProps({})}
        >
          {row.isExpanded ? (
            // @ts-ignore
            <Icon as={AiFillCaretDown} w="16px" h="16px" />
          ) : (
            // @ts-ignore
            <Icon as={AiFillCaretRight} w="16px" h="16px" />
          )}

          {isJobLink && jobId ? (
            <Link
              href={`/jobs/${jobId}`}
              target="_blank"
              rel="noreferrer"
              _hover={{ textDecor: 'underline' }}
            >
              {displayValue}
            </Link>
          ) : (
            displayValue
          )}
        </KText>
      );
    },
    columnConfig,
    title,
  };
};
